/**
 * ExportDialog — export façon « Enregistrer pour l'impression » d'Illustrator.
 *
 * Deux chemins :
 *   - PDF/PNG : service de rendu (headless Chrome sur /print) → raster fidèle
 *     au pixel près. SVG : moteur vectoriel serveur (build_svg).
 *   - PDF PRÊT-PRESSE (préréglages FOGRA) : le PDF du rendu est post-traité
 *     par le backend Print Pro — conversion CMJN avec profil ICC, norme PDF/X,
 *     TrimBox/BleedBox, traits de coupe vectoriels — puis un RAPPORT DE
 *     COUVERTURE D'ENCRE mesuré (TAC max réel vs plafond du profil) s'affiche.
 *
 * Le préréglage pilote tout (toute modification manuelle passe en
 * « Personnalisé »), comme dans Illustrator. Le préréglage par défaut
 * « Qualité maximale (RVB) » = comportement historique, inchangé.
 */
import { useState, useEffect } from 'react';
import { useLabelStore } from '../store/useLabelStore';
import { renderLabelExport, exportLabel, finishPrintPro } from '../api/client';
import { collectExportFonts } from '../utils/exportFonts';
// Pas de copie locale de downloadBlob : celle qui vivait ici gardait les deux
// défauts corrigés en #131 (ancre jamais insérée → Firefox ignore le clic ;
// révocation immédiate → Safari annule le téléchargement). La fenêtre annonçait
// « fichier(s) dans vos téléchargements » et rien n'arrivait (retour de Louise,
// 2026-10-01).
import { downloadBlob } from '../utils/download';
import { calquesDecoupe, elementsAPeindre, refusDecoupe, cheminsDecoupe, encreDe } from '../utils/decoupeExport';

const NOM_ENCRE = { C: 'cyan', M: 'magenta', J: 'jaune', N: 'noir' };

const DPI_CHOICES = [
    { value: 150, label: '150', hint: 'Web / brouillon' },
    { value: 300, label: '300', hint: 'Norme presse' },
    { value: 600, label: '600', hint: 'Haute définition' },
];

// Miroir des profils/préréglages du backend (app/print_pro.py). La liste
// réellement disponible est vérifiée côté serveur (erreur claire si profil
// absent de l'image) — GET /api/export/print-capabilities existe si un jour
// on veut la charger dynamiquement.
const ICC_PROFILES = {
    psocoated_v3:     { label: 'PSO Coated v3 — FOGRA51',      tac: 300, hint: 'Couché premium, norme actuelle (ISO 12647-2:2013)' },
    isocoated_v2_300: { label: 'ISO Coated v2 300% — FOGRA39', tac: 300, hint: 'Couché classique, encrage plafonné 300 %' },
    isocoated_v2:     { label: 'ISO Coated v2 — FOGRA39',      tac: 330, hint: 'Couché classique, encrage 330 %' },
};

const STANDARDS = [
    { id: 'none',   label: 'Aucune',      hint: 'PDF classique (RVB, sans conversion)' },
    { id: 'pdfx1a', label: 'PDF/X-1a',    hint: 'CMJN aplati — accepté partout (2001)' },
    { id: 'pdfx3',  label: 'PDF/X-3',     hint: 'CMJN + gestion ICC (2003)' },
    { id: 'pdfx4',  label: 'PDF/X-4',     hint: 'Moderne — ICC natif (2010) — recommandé' },
];

const INTENTS = [
    { id: 'perceptual', label: 'Perceptuelle' },
    { id: 'relative',   label: 'Colorimétrique relative' },
    { id: 'saturation', label: 'Saturation' },
    { id: 'absolute',   label: 'Colorimétrique absolue' },
];

// PDF/X-4 : norme préconisée par LabelForge (2026-10-08). Miroir de PRESETS
// dans backend/app/print_pro.py — les deux listes changent ensemble.
const PRESETS = {
    label_pro_fogra51: {
        label: 'Étiquette Pro — PSO Coated v3 (FOGRA51)',
        print: { standard: 'pdfx4', profile: 'psocoated_v3', intent: 'relative', compress: 'flate', jpegQuality: 92, marks: true, marksOffset: 3, spotCut: true },
    },
    press_fogra39_300: {
        label: 'Presse — ISO Coated v2 300% (FOGRA39, X-1a)',
        print: { standard: 'pdfx1a', profile: 'isocoated_v2_300', intent: 'relative', compress: 'flate', jpegQuality: 92, marks: true, marksOffset: 3, spotCut: true },
    },
    press_fogra39: {
        label: 'Presse classique — ISO Coated v2 (FOGRA39)',
        print: { standard: 'pdfx4', profile: 'isocoated_v2', intent: 'relative', compress: 'flate', jpegQuality: 92, marks: true, marksOffset: 3, spotCut: true },
    },
    rgb_max: {
        label: 'Qualité maximale (RVB, sans conversion)',
        print: { standard: 'none', profile: 'psocoated_v3', intent: 'relative', compress: 'flate', jpegQuality: 92, marks: false, marksOffset: 3, spotCut: true },
    },
};

const STD_SHORT = { pdfx1a: 'X1a', pdfx3: 'X3', pdfx4: 'X4' };

const TABS = [
    { id: 'general',  label: 'Général' },
    { id: 'compress', label: 'Compression' },
    { id: 'marks',    label: 'Repères & fond perdu' },
    { id: 'output',   label: 'Sortie (couleur)' },
];

function buildFilename(canvas, ext, print) {
    const w = Math.round(canvas?.width_mm || 0);
    const h = Math.round(canvas?.height_mm || 0);
    const stamp = new Date().toISOString().slice(0, 10);
    if (print && print.standard !== 'none') {
        return `labelforge_${STD_SHORT[print.standard] || 'print'}_${print.profile}_${w}x${h}mm_${stamp}.${ext}`;
    }
    return `labelforge_${w}x${h}mm_${stamp}.${ext}`;
}

/** Formate une durée en ms → "M:SS" (ex 73000 → "1:13"). */
function fmtDuration(ms) {
    const s = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Durée d'export attendue (secondes), pour la barre de progression estimée et
 * l'indication « ~Xs ». Approximation volontaire : le rendu vectoriel (page.pdf)
 * est plus lent que le raster, la conversion prêt-presse (CMJN/ICC) ajoute du
 * temps, et un premier export « à froid » réveille le serveur (≈ +15 s). On
 * majore donc un peu pour ne pas paraître bloqué juste après l'estimation.
 */
function estimateExportSec({ formats, engine, wantPro, spotActive }) {
    let s = 8; // réveil/échauffement de base
    if (formats.pdf || formats.png) s += engine === 'vector' ? 30 : 14;
    if (formats.pdf && wantPro) s += 9;     // conversion CMJN/ICC + rapport d'encres
    else if (formats.pdf && spotActive) s += 3;   // ton direct seul, sans conversion
    if (formats.svg) s += 4;
    return s;
}

export default function ExportDialog({ isOpen, onClose }) {
    const getProjectData = useLabelStore((s) => s.getProjectData);
    const canvas = useLabelStore((s) => s.canvas);
    const elements = useLabelStore((s) => s.elements);

    const [preset, setPreset] = useState('rgb_max');
    const [tab, setTab] = useState('general');
    const [formats, setFormats] = useState({ pdf: true, png: false, svg: false });
    const [dpi, setDpi] = useState(300);
    const [includeBleed, setIncludeBleed] = useState(false);
    // Une étiquette à imprimer sort AVEC son fond perdu (1,5 mm par côté depuis
    // le 2026-10-12) : la case se coche à chaque ouverture dès que le document en
    // a un. Décochée par défaut, un document créé avec son fond perdu le perdait
    // à l'export sans que rien ne le dise.
    useEffect(() => {
        if (isOpen) setIncludeBleed((canvas?.bleed_mm || 0) > 0);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
    const [print, setPrint] = useState({ ...PRESETS.rgb_max.print });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [report, setReport] = useState(null);
    // Moteur de rendu PDF : 'raster' (capture, défaut, éprouvé) | 'vector' (page.pdf).
    const [renderEngine, setRenderEngine] = useState('raster');
    // Vectoriser les polices (contours) — n'a de sens qu'en moteur vectoriel + prêt-presse.
    const [vectorizeFonts, setVectorizeFonts] = useState(false);
    // Texte en dégradé à l'export vectoriel : 'raster' (image haute-déf du seul
    // texte dégradé, sûr/par défaut) | 'vector' (SVG vectoriel éditable, bêta).
    // Chromium (page.pdf) ne vectorise pas proprement un dégradé-sur-texte.
    const [gradientTextMode, setGradientTextMode] = useState('raster');
    // Chrono d'export : temps écoulé (ms), tick toutes les 250 ms pendant `busy`.
    const [elapsedMs, setElapsedMs] = useState(0);

    // Démarre/arrête le chrono quand un export tourne. Remis à 0 à chaque export.
    useEffect(() => {
        if (!busy) return;
        const t0 = Date.now();
        setElapsedMs(0);
        const iv = setInterval(() => setElapsedMs(Date.now() - t0), 250);
        return () => clearInterval(iv);
    }, [busy]);

    if (!isOpen) return null;

    const bleedMm = canvas?.bleed_mm || 0;
    const atLeastOne = formats.pdf || formats.png || formats.svg;
    const wantPro = print.standard !== 'none';
    const profile = ICC_PROFILES[print.profile] || {};
    // Dans un PDF, les calques de découpe ne sont JAMAIS fondus dans l'image ;
    // ceux qui sont visibles y reviennent en ton direct, quel que soit le
    // préréglage. Le PNG les garde peints, en repère.
    const { tous: cutAll, imprimes: cutLayers } = calquesDecoupe(elements);
    const spotActive = print.spotCut && cutLayers.length > 0;
    const cutRefus = spotActive ? refusDecoupe(cutLayers) : null;
    const cutEncre = cutLayers.length > 0 ? encreDe(cutLayers[0]) : 'M';
    const expectedSec = estimateExportSec({ formats, engine: renderEngine, wantPro, spotActive });
    // Progression estimée (jamais 100 % tant que pas réellement fini) : rassure
    // sans mentir. Passe à 100 % seulement quand tous les jobs sont « done ».
    const estPct = Math.min(96, Math.round((elapsedMs / 1000 / expectedSec) * 100));
    const overdue = busy && elapsedMs / 1000 > expectedSec * 1.4;

    const toggleFormat = (k) => setFormats((f) => ({ ...f, [k]: !f[k] }));

    const applyPreset = (id) => {
        setPreset(id);
        const p = PRESETS[id];
        if (p) {
            setPrint({ ...p.print });
            if (p.print.standard !== 'none') setFormats((f) => ({ ...f, pdf: true }));
        }
    };
    // Toute modification manuelle d'un réglage print → préréglage « Personnalisé »
    const setPrintField = (patch) => { setPrint((p) => ({ ...p, ...patch })); setPreset('custom'); };

    const exportSvg = async (project) => {
        const c = project.canvas;
        const exportElements = [];
        if (project.aiImage && project.aiImage.url) {
            exportElements.push({
                id: 'ai_background', type: 'image', url: project.aiImage.url,
                x: project.aiImage.x || 0, y: project.aiImage.y || 0,
                width: project.aiImage.width || c.width_mm, height: project.aiImage.height || c.height_mm,
                opacity: 90, rotation: 0,
            });
        }
        exportElements.push(...(project.elements || []).filter((e) => e.type !== 'guide' && !e.guide));
        const payload = {
            document: { width: c.width_mm, height: c.height_mm, backgroundColor: c.color_bg },
            elements: exportElements, format: 'svg', vectorize_text: false,
        };
        return exportLabel(payload, 'svg');
    };

    const handleClose = () => {
        if (busy) return;
        setJobs([]);
        setError(null);
        setReport(null);
        onClose();
    };

    const allDone = jobs.length > 0 && jobs.every((j) => j.status === 'done');

    const handleExport = async () => {
        if (!atLeastOne || busy) return;
        // Une découpe qu'on ne saurait pas poser telle qu'on la voit (tournée, ou
        // deux encres pour un seul ton direct) : refusée AVANT tout rendu.
        if (formats.pdf && cutRefus) { setError(cutRefus); return; }
        setBusy(true);
        setError(null);
        setReport(null);
        const chosen = [];
        if (formats.pdf) chosen.push('pdf');
        if (formats.png) chosen.push('png');
        if (formats.svg) chosen.push('svg');
        setJobs(chosen.map((f) => ({ fmt: f, status: 'pending' })));
        try {
            const project = getProjectData();
            const needsRaster = formats.pdf || formats.png;
            let fonts = null;
            if (needsRaster) fonts = await collectExportFonts(project).catch(() => null);
            const effBleed = includeBleed && bleedMm > 0;

            for (const fmt of chosen) {
                const firstStep = fmt === 'svg' ? 'Génération du SVG…' : 'Rendu de l\'étiquette…';
                setJobs((js) => js.map((x) => (x.fmt === fmt ? { ...x, status: 'running', step: firstStep } : x)));
                try {
                    let blob;
                    if (fmt === 'svg') {
                        blob = await exportSvg(project);
                    } else {
                        // PDF : les calques de découpe ne sont JAMAIS fondus dans l'image
                        // (le préréglage RVB les y laissait, et le trait rose partait à
                        // l'impression) ; ils reviennent en séparation vectorielle
                        // /CutContour, posée par le backend. PNG : peints, en repère.
                        const useSpot = fmt === 'pdf' && spotActive;
                        const renderProject = cutAll.length > 0
                            ? { ...project, elements: elementsAPeindre(project.elements, fmt) }
                            : project;
                        blob = await renderLabelExport({ project: renderProject, format: fmt, dpi, includeBleed: effBleed, fonts, engine: renderEngine, gradientText: renderEngine === 'vector' ? gradientTextMode : 'raster' });
                        // PDF prêt-presse : post-traitement CMJN/ICC/PDF-X + rapport d'encres.
                        // PDF RVB avec découpe : le même post-traitement, sans conversion
                        // (standard « none »), pose la TrimBox et le seul ton direct.
                        if (fmt === 'pdf' && (wantPro || useSpot)) {
                            setJobs((js) => js.map((x) => (x.fmt === fmt ? { ...x, step: wantPro ? 'Conversion couleur (prêt-presse)…' : 'Découpe en ton direct…' } : x)));
                            const res = await finishPrintPro(blob, {
                                standard: print.standard,
                                profile: print.profile,
                                intent: print.intent,
                                compress: print.compress,
                                jpeg_quality: print.jpegQuality,
                                trim_w_mm: canvas.width_mm,
                                trim_h_mm: canvas.height_mm,
                                bleed_mm: effBleed ? bleedMm : 0,
                                marks: wantPro && print.marks,
                                marks_offset_mm: print.marksOffset,
                                // Vectoriser les polices : seulement en moteur vectoriel
                                // (en raster le PDF est déjà une image, aucun texte à vectoriser).
                                vectorize_fonts: renderEngine === 'vector' && vectorizeFonts,
                                cut_paths: useSpot ? cheminsDecoupe(cutLayers) : null,
                                ink_report: wantPro,
                                title: `LabelForge — ${Math.round(canvas.width_mm)}x${Math.round(canvas.height_mm)}mm`,
                            });
                            blob = res.blob;
                            setReport(res.report);
                        }
                    }
                    downloadBlob(blob, buildFilename(canvas, fmt, fmt === 'pdf' ? print : null));
                    setJobs((js) => js.map((x) => (x.fmt === fmt ? { ...x, status: 'done' } : x)));
                } catch (err) {
                    setJobs((js) => js.map((x) => (x.fmt === fmt ? { ...x, status: 'error' } : x)));
                    throw err;
                }
            }
        } catch (e) {
            setError(e.message || "Échec de l'export");
        } finally {
            setBusy(false);
        }
    };

    const Toggle = ({ on, onChange, disabled }) => (
        <button
            onClick={() => !disabled && onChange(!on)}
            disabled={disabled}
            className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${disabled ? 'bg-white/5 cursor-not-allowed' : on ? 'bg-forge-mauve/60' : 'bg-white/10'}`}
        >
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${on && !disabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </button>
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center modal-backdrop-strong backdrop-blur-sm p-4">
            <div
                className="w-full max-w-2xl bg-forge-bg border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-black/20">
                    <div className="flex items-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-forge-mauve">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        <h2 className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-white/70">Exporter l'étiquette</h2>
                    </div>
                    <button onClick={handleClose} disabled={busy} className="w-6 h-6 rounded-md flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Préréglage — pilote tous les réglages, façon Illustrator */}
                <div className="px-5 pt-4 pb-3 border-b border-white/5 flex items-center gap-3">
                    <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 shrink-0">Préréglage</span>
                    <select
                        value={preset}
                        onChange={(e) => applyPreset(e.target.value)}
                        className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[11px] font-sans text-white/90 outline-none focus:border-forge-mauve/40 transition-colors"
                    >
                        {Object.entries(PRESETS).map(([id, p]) => (
                            <option key={id} value={id}>{p.label}</option>
                        ))}
                        {preset === 'custom' && <option value="custom">Personnalisé</option>}
                    </select>
                    {wantPro && (
                        <span className="shrink-0 px-2 py-1 rounded-md text-[8px] font-mono font-bold tracking-widest uppercase bg-forge-mauve/15 border border-forge-mauve/30 text-forge-mauve">
                            Prêt-presse
                        </span>
                    )}
                </div>

                {/* Corps : onglets verticaux + contenu */}
                <div className="flex" style={{ minHeight: 290 }}>
                    <div className="w-44 shrink-0 border-r border-white/5 bg-black/10 py-3">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                className={`w-full text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-wider transition-all border-l-2 ${
                                    tab === t.id
                                        ? 'border-forge-mauve text-white bg-white/[0.04]'
                                        : 'border-transparent text-white/40 hover:text-white/75'
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 px-5 py-4 overflow-y-auto custom-scrollbar" style={{ maxHeight: 380 }}>
                        {tab === 'general' && (
                            <>
                                <div className="mb-5">
                                    <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2 block">Formats</span>
                                    <div className="flex gap-2">
                                        {[['pdf', 'PDF'], ['png', 'PNG'], ['svg', 'SVG']].map(([k, lbl]) => (
                                            <button
                                                key={k}
                                                onClick={() => toggleFormat(k)}
                                                className={`flex-1 py-2.5 rounded-lg text-[11px] font-mono uppercase tracking-widest border transition-all ${
                                                    formats[k]
                                                        ? 'bg-forge-mauve/20 border-forge-mauve/50 text-forge-mauve'
                                                        : 'bg-black/30 border-white/10 text-white/40 hover:text-white/70 hover:border-white/20'
                                                }`}
                                            >
                                                {formats[k] ? '✓ ' : ''}{lbl}
                                            </button>
                                        ))}
                                    </div>
                                    {wantPro && (
                                        <div className="text-[8px] font-mono text-white/35 mt-1.5">
                                            Le préréglage prêt-presse s'applique au PDF. PNG/SVG restent des exports écran (RVB).
                                        </div>
                                    )}
                                </div>

                                <div className="mb-2">
                                    <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2 block">Définition</span>
                                    <div className="flex gap-2">
                                        {DPI_CHOICES.map((c) => (
                                            <button
                                                key={c.value}
                                                onClick={() => setDpi(c.value)}
                                                className={`flex-1 py-2 rounded-lg border transition-all ${
                                                    dpi === c.value
                                                        ? 'bg-forge-mauve/20 border-forge-mauve/50'
                                                        : 'bg-black/30 border-white/10 hover:border-white/20'
                                                }`}
                                            >
                                                <div className={`text-[13px] font-mono font-bold ${dpi === c.value ? 'text-forge-mauve' : 'text-white/70'}`}>{c.label}</div>
                                                <div className="text-[8px] font-mono text-white/35 mt-0.5">{c.hint}</div>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="text-[8px] font-mono text-white/30 mt-1.5">300 DPI = norme presse pour les étiquettes. Le SVG est vectoriel (DPI sans objet).</div>
                                </div>
                            </>
                        )}

                        {tab === 'compress' && (
                            <>
                                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2 block">Compression de l'image (PDF prêt-presse)</span>
                                {!wantPro && (
                                    <div className="text-[9px] font-mono text-white/35 mb-3">Disponible avec un préréglage prêt-presse (norme PDF/X choisie dans « Sortie »).</div>
                                )}
                                <div className={`flex gap-2 mb-3 ${!wantPro ? 'opacity-40 pointer-events-none' : ''}`}>
                                    {[['flate', 'Sans perte (Flate)', 'Qualité maximale — défaut presse'], ['jpeg', 'JPEG', 'Fichier plus léger']].map(([k, lbl, hint]) => (
                                        <button
                                            key={k}
                                            onClick={() => setPrintField({ compress: k })}
                                            className={`flex-1 py-2.5 px-2 rounded-lg border transition-all text-left ${
                                                print.compress === k
                                                    ? 'bg-forge-mauve/20 border-forge-mauve/50'
                                                    : 'bg-black/30 border-white/10 hover:border-white/20'
                                            }`}
                                        >
                                            <div className={`text-[10px] font-mono uppercase tracking-wider ${print.compress === k ? 'text-forge-mauve' : 'text-white/70'}`}>{lbl}</div>
                                            <div className="text-[8px] font-mono text-white/35 mt-0.5">{hint}</div>
                                        </button>
                                    ))}
                                </div>
                                {wantPro && print.compress === 'jpeg' && (
                                    <div className="flex items-center gap-3">
                                        <span className="text-[9px] font-mono uppercase tracking-widest text-white/50">Qualité</span>
                                        <input
                                            type="range" min="80" max="100" value={print.jpegQuality}
                                            onChange={(e) => setPrintField({ jpegQuality: Number(e.target.value) })}
                                            className="flex-1 h-1 bg-black/20 rounded-lg appearance-none cursor-pointer accent-white"
                                        />
                                        <span className="text-[11px] font-mono text-white/80 w-8 text-right">{print.jpegQuality}</span>
                                    </div>
                                )}
                                <div className="text-[8px] font-mono text-white/30 mt-3">L'image n'est jamais rééchantillonnée : la définition choisie dans « Général » est conservée telle quelle.</div>
                            </>
                        )}

                        {tab === 'marks' && (
                            <>
                                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2 block">Repères d'impression (PDF prêt-presse)</span>
                                <div className={`${!wantPro ? 'opacity-40 pointer-events-none' : ''}`}>
                                    <div className="flex items-center justify-between px-3 py-2.5 rounded-lg border bg-black/30 border-white/10 mb-2">
                                        <div>
                                            <div className="text-[11px] font-mono uppercase tracking-wider text-white/75">Traits de coupe</div>
                                            <div className="text-[8px] font-mono text-white/35 mt-0.5">Vectoriels, noir 100 K, 0,25 pt — hors zone de coupe</div>
                                        </div>
                                        {/* Des traits de coupe sans fond perdu laissent un filet blanc au
                                            moindre écart de coupe : les activer active le fond perdu,
                                            quand le document en déclare un. Louise cochait les traits
                                            et attendait le fond perdu avec (retour du 2026-10-01). */}
                                        <Toggle on={print.marks} onChange={(v) => { setPrintField({ marks: v }); if (v && bleedMm > 0) setIncludeBleed(true); }} />
                                    </div>
                                    {print.marks && (
                                        <div className="flex items-center gap-3 px-3 py-2 mb-2">
                                            <span className="text-[9px] font-mono uppercase tracking-widest text-white/50">Décalage</span>
                                            <input
                                                type="range" min="1" max="8" step="0.5" value={print.marksOffset}
                                                onChange={(e) => setPrintField({ marksOffset: Number(e.target.value) })}
                                                className="flex-1 h-1 bg-black/20 rounded-lg appearance-none cursor-pointer accent-white"
                                            />
                                            <span className="text-[11px] font-mono text-white/80 w-14 text-right">{print.marksOffset} mm</span>
                                        </div>
                                    )}
                                </div>

                                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2 mt-4 block">Fond perdu</span>
                                <div className="flex items-center justify-between px-3 py-2.5 rounded-lg border bg-black/30 border-white/10">
                                    <div>
                                        <div className={`text-[11px] font-mono uppercase tracking-wider ${bleedMm === 0 ? 'text-white/30' : 'text-white/75'}`}>Inclure le fond perdu</div>
                                        <div className="text-[8px] font-mono text-white/35 mt-0.5">
                                            {bleedMm > 0 ? `${bleedMm} mm de débord (BleedBox normée dans le PDF)` : 'Aucun fond perdu défini sur ce document — Préférences ▸ Fond perdu'}
                                        </div>
                                    </div>
                                    <Toggle on={includeBleed && bleedMm > 0} onChange={(v) => setIncludeBleed(v)} disabled={bleedMm === 0} />
                                </div>
                                <div className="text-[8px] font-mono text-white/30 mt-2">Les repères de marge à l'écran ne sont jamais imprimés. La TrimBox (format fini) est toujours posée dans le PDF prêt-presse.</div>
                            </>
                        )}

                        {tab === 'output' && (
                            <>
                                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2 block">Moteur de rendu (PDF)</span>
                                <div className="flex gap-2 mb-4">
                                    {[
                                        { id: 'raster', label: 'Raster (actuel)', hint: 'Capture fidèle de l\'éditeur — sûr, éprouvé' },
                                        { id: 'vector', label: 'Vectoriel (pro · bêta)', hint: 'Texte vectoriel + polices embarquées (page.pdf)' },
                                    ].map((e) => (
                                        <button
                                            key={e.id}
                                            onClick={() => setRenderEngine(e.id)}
                                            className={`flex-1 py-2 px-2 rounded-lg border transition-all text-left ${
                                                renderEngine === e.id
                                                    ? 'bg-forge-mauve/20 border-forge-mauve/50'
                                                    : 'bg-black/30 border-white/10 hover:border-white/20'
                                            }`}
                                        >
                                            <div className={`text-[10px] font-mono font-bold uppercase tracking-wider ${renderEngine === e.id ? 'text-forge-mauve' : 'text-white/70'}`}>{e.label}</div>
                                            <div className="text-[8px] font-mono text-white/35 mt-0.5 leading-relaxed">{e.hint}</div>
                                        </button>
                                    ))}
                                </div>

                                {/* Texte en dégradé — choix image/vectoriel, seulement en moteur vectoriel */}
                                {renderEngine === 'vector' && (
                                    <div className="mb-4">
                                        <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2 block">Texte en dégradé</span>
                                        <div className="flex gap-2">
                                            {[
                                                { id: 'raster', label: 'Image haute-déf (sûr)', hint: 'Le seul texte dégradé en image ~400 DPI — correct dans Illustrator/Acrobat/Aperçu' },
                                                { id: 'vector', label: 'Vectoriel (éditable · bêta)', hint: 'Texte dégradé en SVG vectoriel éditable — calage à vérifier' },
                                            ].map((e) => (
                                                <button
                                                    key={e.id}
                                                    onClick={() => setGradientTextMode(e.id)}
                                                    className={`flex-1 py-2 px-2 rounded-lg border transition-all text-left ${
                                                        gradientTextMode === e.id
                                                            ? 'bg-forge-mauve/20 border-forge-mauve/50'
                                                            : 'bg-black/30 border-white/10 hover:border-white/20'
                                                    }`}
                                                >
                                                    <div className={`text-[10px] font-mono font-bold uppercase tracking-wider ${gradientTextMode === e.id ? 'text-forge-mauve' : 'text-white/70'}`}>{e.label}</div>
                                                    <div className="text-[8px] font-mono text-white/35 mt-0.5 leading-relaxed">{e.hint}</div>
                                                </button>
                                            ))}
                                        </div>
                                        <div className="text-[8px] font-mono text-white/30 mt-1.5">Ne concerne que le texte avec remplissage en dégradé (ex. titre). Le reste du texte reste vectoriel.</div>
                                    </div>
                                )}

                                {/* Vectoriser les polices (contours) — actif seulement en vectoriel + prêt-presse */}
                                <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg border bg-black/30 border-white/10 mb-4 ${(renderEngine !== 'vector' || !wantPro) ? 'opacity-50' : ''}`}>
                                    <div>
                                        <div className="text-[11px] font-mono uppercase tracking-wider text-white/75">Vectoriser les polices</div>
                                        <div className="text-[8px] font-mono text-white/35 mt-0.5 leading-relaxed">
                                            {renderEngine !== 'vector'
                                                ? 'Disponible uniquement en moteur vectoriel'
                                                : !wantPro
                                                    ? 'Nécessite un préréglage prêt-presse (norme PDF/X)'
                                                    : 'Texte → tracés (comme Illustrator) : zéro dépendance police, mais texte non éditable'}
                                        </div>
                                    </div>
                                    <Toggle
                                        on={vectorizeFonts && renderEngine === 'vector' && wantPro}
                                        onChange={(v) => setVectorizeFonts(v)}
                                        disabled={renderEngine !== 'vector' || !wantPro}
                                    />
                                </div>

                                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2 block">Norme PDF/X</span>
                                <div className="flex gap-2 mb-4">
                                    {STANDARDS.map((s) => (
                                        <button
                                            key={s.id}
                                            onClick={() => setPrintField({ standard: s.id })}
                                            className={`flex-1 py-2 px-2 rounded-lg border transition-all text-left ${
                                                print.standard === s.id
                                                    ? 'bg-forge-mauve/20 border-forge-mauve/50'
                                                    : 'bg-black/30 border-white/10 hover:border-white/20'
                                            }`}
                                        >
                                            <div className={`text-[10px] font-mono font-bold uppercase tracking-wider ${print.standard === s.id ? 'text-forge-mauve' : 'text-white/70'}`}>{s.label}</div>
                                            <div className="text-[8px] font-mono text-white/35 mt-0.5 leading-relaxed">{s.hint}</div>
                                        </button>
                                    ))}
                                </div>

                                <div className={`${!wantPro ? 'opacity-40 pointer-events-none' : ''}`}>
                                    <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2 block">Profil ICC de destination (CMJN)</span>
                                    <div className="space-y-1.5 mb-4">
                                        {Object.entries(ICC_PROFILES).map(([id, p]) => (
                                            <button
                                                key={id}
                                                onClick={() => setPrintField({ profile: id })}
                                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-all text-left ${
                                                    print.profile === id
                                                        ? 'bg-forge-mauve/15 border-forge-mauve/40'
                                                        : 'bg-black/30 border-white/10 hover:border-white/20'
                                                }`}
                                            >
                                                <div>
                                                    <div className={`text-[10px] font-mono ${print.profile === id ? 'text-forge-mauve' : 'text-white/75'}`}>{p.label}</div>
                                                    <div className="text-[8px] font-mono text-white/35 mt-0.5">{p.hint}</div>
                                                </div>
                                                <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-black/40 border border-white/10 text-white/50 shrink-0">TAC {p.tac} %</span>
                                            </button>
                                        ))}
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 shrink-0">Intention de rendu</span>
                                        <select
                                            value={print.intent}
                                            onChange={(e) => setPrintField({ intent: e.target.value })}
                                            className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-[11px] font-sans text-white/90 outline-none focus:border-forge-mauve/40"
                                        >
                                            {INTENTS.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="text-[8px] font-mono text-white/30 mt-2">Le profil est embarqué dans le PDF (OutputIntent) — l'imprimeur voit exactement la condition visée. La saturation d'encre maximale (TAC) est portée par le profil, et mesurée après export.</div>
                                </div>

                                {/* Hors du bloc prêt-presse : la découpe part en ton direct avec
                                    TOUS les préréglages, le RVB compris. */}
                                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2 mt-4 block">Finition étiquette</span>
                                <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg border bg-black/30 border-white/10 ${cutLayers.length === 0 ? 'opacity-50' : ''}`}>
                                    <div>
                                        <div className="text-[11px] font-mono uppercase tracking-wider text-white/75">Découpe en ton direct</div>
                                        <div className="text-[8px] font-mono text-white/35 mt-0.5 leading-relaxed">
                                            {cutLayers.length > 0
                                                ? `CutContour, ${NOM_ENCRE[cutEncre]} 100 %, vectoriel en surimpression — ${cutLayers.length} tracé${cutLayers.length > 1 ? 's' : ''}`
                                                : 'Aucun calque de découpe visible sur ce document'}
                                            {cutAll.length > cutLayers.length && ` · ${cutAll.length - cutLayers.length} masqué${cutAll.length - cutLayers.length > 1 ? 's' : ''}, non découpé${cutAll.length - cutLayers.length > 1 ? 's' : ''}`}
                                            {cutAll.length > 0 && ' · visible à l\'écran, non imprimé · gardé en repère dans le PNG'}
                                        </div>
                                    </div>
                                    <Toggle
                                        on={print.spotCut && cutLayers.length > 0}
                                        onChange={(v) => setPrintField({ spotCut: v })}
                                        disabled={cutLayers.length === 0}
                                    />
                                </div>
                                {cutRefus && formats.pdf && (
                                    <div className="text-[9px] font-mono text-orange-300 mt-2 leading-relaxed">{cutRefus}</div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Pied : rapport d'encres, progression, erreurs, actions */}
                <div className="px-5 py-4 border-t border-white/5 bg-black/10">
                    {report && (
                        <div className="mb-3 p-3 rounded-lg bg-black/30 border border-white/10">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50">Rapport prêt-presse</span>
                                <span className="text-[9px] font-mono text-white/60">{report.standard} · {report.profile}{report.fonts ? ` · polices ${report.fonts}` : ''}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                {report.tac_max != null && (
                                    <div className={`px-2.5 py-1.5 rounded-md border text-center ${
                                        report.tac_limit && report.tac_max > report.tac_limit
                                            ? 'bg-orange-500/10 border-orange-500/40 text-orange-300'
                                            : 'bg-green-500/10 border-green-500/30 text-green-300'
                                    }`}>
                                        <div className="text-[13px] font-mono font-bold">{report.tac_max} %</div>
                                        <div className="text-[7px] font-mono uppercase tracking-widest opacity-80">Encrage max{report.tac_limit ? ` / ${report.tac_limit}` : ''}</div>
                                    </div>
                                )}
                                {report.avg && (
                                    <div className="flex-1 grid grid-cols-4 gap-2">
                                        {[['C', report.avg.c, '#00b7eb'], ['M', report.avg.m, '#ec008c'], ['J', report.avg.y, '#ffd400'], ['N', report.avg.k, '#888888']].map(([l, v, col]) => (
                                            <div key={l}>
                                                <div className="flex justify-between text-[8px] font-mono text-white/50 mb-0.5"><span>{l}</span><span>{v} %</span></div>
                                                <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                                                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, v)}%`, backgroundColor: col }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {report.conformite && (
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    <span className={`text-[8px] font-mono px-1.5 py-0.5 rounded border ${
                                        report.conformite.verdict === 'ok'
                                            ? 'bg-green-500/10 border-green-500/30 text-green-300'
                                            : report.conformite.verdict === 'a_verifier'
                                                ? 'bg-orange-500/10 border-orange-500/40 text-orange-300'
                                                : 'bg-white/5 border-white/10 text-white/50'
                                    }`}>
                                        Conformité auto : {report.conformite.verdict === 'ok' ? 'OK'
                                            : report.conformite.verdict === 'a_verifier' ? 'à vérifier' : 'indéterminée'}
                                    </span>
                                    {report.conformite.pdfx && <span className="text-[8px] font-mono text-white/40">{report.conformite.pdfx}</span>}
                                    {report.conformite.output_intent && <span className="text-[8px] font-mono text-white/40">OI&nbsp;: {report.conformite.output_intent}</span>}
                                    {(report.conformite.modeles || []).length > 0 && <span className="text-[8px] font-mono text-white/40">{report.conformite.modeles.join('+')}</span>}
                                </div>
                            )}
                            {(report.conformite?.alertes || []).map((a, i) => (
                                <div key={`cf${i}`} className="mt-1 text-[9px] font-mono text-orange-300">⚠ {a}</div>
                            ))}
                            {(report.warnings || []).map((w, i) => (
                                <div key={i} className="mt-2 text-[9px] font-mono text-orange-300">⚠ {w}</div>
                            ))}
                        </div>
                    )}

                    {jobs.length > 0 && (
                        <div className="mb-3 space-y-2">
                            {jobs.map((job) => {
                                // Barre estimée pour le job en cours (rassure sans mentir :
                                // plafonnée à 96 % tant que pas réellement terminé).
                                const w = job.status === 'done' || job.status === 'error'
                                    ? 100
                                    : job.status === 'running' ? Math.max(6, estPct) : 0;
                                const barColor = job.status === 'done' ? 'bg-green-400'
                                    : job.status === 'error' ? 'bg-red-400'
                                        : job.status === 'running' ? 'bg-forge-mauve' : 'bg-white/20';
                                return (
                                    <div key={job.fmt}>
                                        <div className="flex items-center gap-2">
                                            <span className="w-9 text-[10px] font-mono text-white/60">{job.fmt.toUpperCase()}</span>
                                            <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full transition-all duration-300 ${barColor} ${job.status === 'running' ? 'animate-pulse' : ''}`}
                                                    style={{ width: `${w}%` }}
                                                />
                                            </div>
                                            <span className="w-9 text-right text-[10px] font-mono tabular-nums">
                                                {job.status === 'done' ? <span className="text-green-400">✓</span>
                                                    : job.status === 'error' ? <span className="text-red-400">✗</span>
                                                        : job.status === 'running' ? <span className="text-forge-mauve">{estPct}%</span>
                                                            : <span className="text-white/20">•</span>}
                                            </span>
                                        </div>
                                        {job.status === 'running' && job.step && (
                                            <div className="pl-11 mt-0.5 text-[8px] font-mono text-white/40">{job.step}</div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Chrono + durée attendue — répond au « est-ce que ça a buggé ? » */}
                            {busy && (
                                <div className="flex items-center gap-2 pl-11 pt-0.5 text-[8px] font-mono text-white/45">
                                    <span className="tabular-nums">⏱ {fmtDuration(elapsedMs)}</span>
                                    <span className="text-white/25">·</span>
                                    <span>généralement ~{expectedSec}s{renderEngine === 'vector' && (formats.pdf) ? ' (vectoriel = plus lent)' : ''}</span>
                                </div>
                            )}
                            {overdue && (
                                <div className="pl-11 text-[8px] font-mono text-orange-300/80">
                                    C'est plus long que d'habitude (gros fichier ou réveil du serveur) — ça tourne toujours, patiente.
                                </div>
                            )}
                            {allDone && (
                                <div className="text-[9px] font-mono text-green-400/80 pt-0.5">✓ Export terminé en {fmtDuration(elapsedMs)} — fichier(s) dans vos téléchargements.</div>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="mb-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-[10px] font-mono text-red-300">⚠ {error}</div>
                    )}

                    <div className="flex gap-2">
                        <button
                            onClick={handleClose}
                            disabled={busy}
                            className="flex-1 py-2.5 border border-white/10 rounded-lg text-[10px] font-mono uppercase tracking-wider text-white/60 hover:text-white hover:border-white/30 transition-all disabled:opacity-40"
                        >
                            {allDone ? 'Fermer' : 'Annuler'}
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={busy || !atLeastOne}
                            className="flex-[2] py-2.5 bg-forge-mauve/20 hover:bg-forge-mauve/30 border border-forge-mauve/40 hover:border-forge-mauve/60 rounded-lg text-[11px] font-mono uppercase tracking-widest text-forge-mauve transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {busy ? 'Export…' : (allDone ? 'Ré-exporter' : (wantPro ? 'Exporter (prêt-presse)' : 'Exporter'))}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
